import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Lock,
  Calendar,
  BookOpen,
  ChevronDown,
  AlertCircle,
  CheckCircle2,
  Pencil,
  Check,
  X
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';

interface AcademicPeriod {
  id: number;
  code: string;
  name: string;
  type: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  programId: number | null;
}

interface Category {
  id: number;
  name: string;
  weightPercent: string;
  minimumPassingScore?: string | null;
  sequence: number;
}

interface EvaluationPlan {
  id: number;
  title: string;
  periodId: number;
  periodName: string;
  courseId: number;
  courseName: string;
  status: string;
  categories: Category[];
}

const periodTypeLabels: Record<string, string> = {
  matutino: 'Matutino',
  vespertino: 'Vespertino',
  sabatino: 'Sabatino',
};

function StatusBadge({ status }: { status: string }) {
  if (status === 'locked') {
    return (
      <Badge variant="outline" className="text-gray-600 border-gray-400">
        <Lock className="w-3 h-3 mr-1" />
        Bloqueado
      </Badge>
    );
  }
  if (status === 'active') {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Activo
      </Badge>
    );
  }
  return null;
}

export default function EvaluationPlans() {
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null);
  const [editingCatId, setEditingCatId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const { data: periods, isLoading: loadingPeriods } = useQuery<AcademicPeriod[]>({
    queryKey: ['academic-periods'],
    queryFn: async () => {
      const response = await axios.get('/api/academic-periods');
      return response.data;
    }
  });

  const { data: plans, isLoading: loadingPlans } = useQuery<EvaluationPlan[]>({
    queryKey: ['evaluation-plans-by-period', selectedPeriodId],
    queryFn: async () => {
      const response = await axios.get(`/api/evaluation/plans?periodId=${selectedPeriodId}`);
      return response.data;
    },
    enabled: !!selectedPeriodId
  });

  const selectedPeriod = periods?.find(p => p.id === selectedPeriodId);

  const totalWeightForPlan = (categories: Category[]) =>
    categories.reduce((sum, c) => sum + parseFloat(c.weightPercent || '0'), 0);

  function startEdit(cat: Category) {
    setEditingCatId(cat.id);
    setEditValue(cat.minimumPassingScore ? parseFloat(cat.minimumPassingScore).toString() : '');
  }

  function cancelEdit() {
    setEditingCatId(null);
    setEditValue('');
  }

  async function saveEdit(catId: number) {
    setSaving(true);
    try {
      const val = editValue.trim();
      const minimumPassingScore = val === '' ? null : parseFloat(val);
      await axios.put(`/api/evaluation/categories/${catId}`, { minimumPassingScore });
      await queryClient.invalidateQueries({ queryKey: ['evaluation-plans-by-period', selectedPeriodId] });
      setEditingCatId(null);
      setEditValue('');
    } catch (e) {
      alert('Error al guardar el mínimo aprobatorio');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Planes de Evaluación</h1>
        <p className="text-gray-500 text-sm mt-1">
          Consulta y configura los planes de evaluación por grupo académico
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-5">
        <div className="max-w-sm">
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            Grupo Académico
          </Label>
          <SearchableSelect
            options={(periods || []).map(p => ({
              value: p.id,
              label: `${p.name}${p.type && periodTypeLabels[p.type] ? ` — ${periodTypeLabels[p.type]}` : p.type ? ` — ${p.type}` : ''}`
            }))}
            value={selectedPeriodId}
            onChange={(val) => setSelectedPeriodId(val ? Number(val) : null)}
            placeholder={loadingPeriods ? 'Cargando grupos…' : 'Buscar grupo…'}
            disabled={loadingPeriods}
          />
        </div>

        {selectedPeriod && (
          <div className="mt-3 flex items-center gap-4 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-2">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4 text-gray-400" />
              {selectedPeriod.startDate && selectedPeriod.endDate
                ? `${selectedPeriod.startDate} → ${selectedPeriod.endDate}`
                : 'Sin fechas definidas'}
            </span>
          </div>
        )}
      </div>

      {!selectedPeriodId ? (
        <div className="text-center py-16 bg-white rounded-lg shadow">
          <ChevronDown className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Selecciona un grupo académico</p>
          <p className="text-sm text-gray-400 mt-1">
            Los planes de evaluación se mostrarán aquí una vez que elijas un grupo
          </p>
        </div>
      ) : loadingPlans ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : !plans || plans.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-lg shadow">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">
            No hay planes de evaluación para este grupo
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Los planes se generan desde la configuración de cada materia en el catálogo
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500">
            {plans.length} {plans.length === 1 ? 'plan' : 'planes'} encontrado{plans.length === 1 ? '' : 's'} para <strong>{selectedPeriod?.name}</strong>
          </p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => {
              const totalWeight = totalWeightForPlan(plan.categories || []);
              return (
                <Card key={plan.id} className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base leading-tight truncate">{plan.title}</CardTitle>
                        <CardDescription className="text-sm mt-0.5 truncate">{plan.courseName}</CardDescription>
                      </div>
                      <StatusBadge status={plan.status} />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{plan.periodName}</span>
                    </div>

                    {plan.categories && plan.categories.length > 0 ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Categorías
                          </span>
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                            totalWeight === 100
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-600'
                          }`}>
                            Total: {totalWeight}%
                          </span>
                        </div>
                        {plan.categories.map((cat) => {
                          const minScore = cat.minimumPassingScore
                            ? parseFloat(cat.minimumPassingScore)
                            : null;
                          const isEditing = editingCatId === cat.id;
                          return (
                            <div
                              key={cat.id}
                              className="group flex items-center justify-between bg-gray-50 border border-gray-100 px-2.5 py-1.5 rounded-md text-sm"
                            >
                              <span className="font-medium text-gray-800 truncate flex-1 mr-2">
                                {cat.name}
                              </span>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className="text-gray-600 font-semibold">
                                  {cat.weightPercent}%
                                </span>
                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500">mín</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="1"
                                      value={editValue}
                                      onChange={e => setEditValue(e.target.value)}
                                      className="w-16 h-6 text-xs text-center px-1 py-0"
                                      placeholder="0-100"
                                      autoFocus
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') saveEdit(cat.id);
                                        if (e.key === 'Escape') cancelEdit();
                                      }}
                                    />
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-5 w-5 text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => saveEdit(cat.id)}
                                      disabled={saving}
                                    >
                                      <Check className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-5 w-5 text-gray-400 hover:text-gray-600"
                                      onClick={cancelEdit}
                                      disabled={saving}
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    {minScore != null && (
                                      <span
                                        className="flex items-center gap-0.5 text-xs bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-medium"
                                        title="Calificación mínima aprobatoria para esta categoría"
                                      >
                                        <AlertCircle className="w-3 h-3" />
                                        mín {minScore}
                                      </span>
                                    )}
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-5 w-5 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 transition-opacity"
                                      title="Editar mínimo aprobatorio"
                                      onClick={() => startEdit(cat)}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Sin categorías definidas</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
